
import blogPostQuery from "~/apollo/queries/blog/post";
import blogPostsQuery from "~/apollo/queries/blog/posts";
import commentsQuery from "~/apollo/queries/comments/findAllFlat";
import createCommentMutation from "~/apollo/mutations/createComment";
import lenisScrollMixin from "@/mixins/lenisScrollMixin";
import previewModeMixin from "@/mixins/previewModeMixin";
import fetchPostMixin from "@/mixins/fetchPostMixin";
import FireIcon from "~/static/icons/fire.svg?inline";
import FacebookIcon from "~/static/icons/facebook.svg?inline";
import LinkedInIcon from "~/static/icons/linkedin.svg?inline";
import XIcon from "~/static/icons/x.svg?inline";
import seoMixin from "@/mixins/seoMixin";
import { retryApolloQuery } from "~/utils/apolloRetry";

export default {
  name: 'BlogPostSlug',
  colorMode: 'light',
  components: {FireIcon, FacebookIcon, LinkedInIcon, XIcon},
  mixins: [lenisScrollMixin, previewModeMixin, fetchPostMixin, seoMixin],
  async asyncData({app, route, store, error}) {
    const previewMode = store.state.previewMode;

    if (previewMode) {
      await app.apolloProvider.defaultClient.resetStore();
    }

    try {
      // Fetch blogPost data with retry mechanism
      const blogPostResponse = await retryApolloQuery(
        app.apolloProvider.defaultClient,
        {
          query: blogPostQuery,
          variables: {
            slug: route.params.slug,
            locale: app.i18n?.locale || 'en',
          },
        },
        {
          maxAttempts: 5,
          onRetry: (error, attempt, delay) => {
            console.log(`Blog post query retry ${attempt}/5 for slug: ${route.params.slug}`)
          }
        }
      );

      const blogPost = blogPostResponse.data.blogPosts.data[0];

      if (!blogPost) {
        return error({ statusCode: 404, message: 'Blog post not found' });
      }

      const blogPostId = blogPost.id;

      // Set route parameters for localization
      app.store.dispatch(
        "i18n/setRouteParams",
        Object.fromEntries(
          blogPost.attributes.localizations.data.map((el) => [
            el.attributes.locale,
            {slug: el.attributes.slug},
          ])
        )
      );

      // Fetch relatedPosts data with retry mechanism
      let relatedPostsResponse = await retryApolloQuery(
        app.apolloProvider.defaultClient,
        {
          query: blogPostsQuery,
          variables: {
            locale: app.i18n?.locale || 'en',
          },
        },
        {
          maxAttempts: 3,
          onRetry: (error, attempt) => {
            console.log(`Related posts query retry ${attempt}/3`)
          }
        }
      );

      const relatedPosts = relatedPostsResponse.data.blogPosts.data;

      // Fetch comments for the blog post with retry mechanism
      const commentsResponse = await retryApolloQuery(
        app.apolloProvider.defaultClient,
        {
          query: commentsQuery,
          variables: {
            relation: `api::blog-post.blog-post:${blogPostId}`,
          },
        },
        {
          maxAttempts: 3,
          onRetry: (error, attempt) => {
            console.log(`Comments query retry ${attempt}/3`)
          }
        }
      );

      const comments = commentsResponse.data.findAllFlat.data;

      return {
        blogPost,
        relatedPosts,
        previewMode,
        comments,
      }
    } catch (err) {
      console.error('Error fetching blog post data:', err);

      // Check if it's a network error or GraphQL error
      if (err.networkError) {
        console.error('Network error details:', err.networkError);
        return error({ statusCode: 503, message: 'Service temporarily unavailable' });
      }

      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        console.error('GraphQL errors:', err.graphQLErrors);
        return error({ statusCode: 400, message: 'Invalid request' });
      }

      // Generic error fallback
      return error({ statusCode: 500, message: 'Internal server error' });
    }
  },
  async fetchComments() {
    const blogPostId = this.blogPost.id;

    const commentsResponse = await this.$apollo.query({
      query: commentsQuery,
      variables: {
        relation: `api::blog-post.blog-post:${blogPostId}`,
      },
      fetchPolicy: 'network-only',
    });

    this.comments = commentsResponse.data.findAllFlat.data;
  },
  data() {
    return {
      blogPost: null,
      relatedPosts: null,
      activeSection: '',
      lenis: null,
      observer: null,
      comments: [],
      newComment: {
        author: {name: '', id: ''},
        content: '',
      },
      formLoading: false,
      formSubmitted: false,
      showCommentsForm: false,
    };
  },
  computed: {
    currentUrl() {
      if (process.server) {
        return process.env.BASE_URL + this.$route.fullPath + '/';
      } else {
        return window.location.origin + this.$route.fullPath;
      }
    },
    shareUrls() {
      const encodedUrl = encodeURIComponent(this.currentUrl);
      const encodedTitle = encodeURIComponent(this.blogPost.attributes?.title || '');
      const appId = '3934656460098189';

      return {
        facebook: `https://www.facebook.com/dialog/feed?app_id=${appId}&link=${encodedUrl}&caption=${encodedTitle}&redirect_uri=${encodedUrl}`,
        linkedin: `https://www.linkedin.com/shareArticle?mini=true&source=Onvocado&title=${encodedTitle}&url=${encodedUrl}`,
        x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      };
    },
  },
  created() {
    const preview = this.$route.query.preview;

    if (preview && !this.blogPost) {
      // Set color as it doesn't automatically set after redirect
      this.$colorMode.preference = 'dark';
      this.fetchPost('blogPosts', blogPostQuery).then(blogPostData => {
        this.blogPost = blogPostData;

        this.fetchRelatedPosts().then(relatedPostsData => {
          this.relatedPosts = relatedPostsData;
          this.$nextTick(() => {
            this.previewMode = true;
          });
        });
      });
    }
  },
  async mounted() {
    try {
      await this.$recaptcha.init()
    } catch (e) {
      console.log(e);
    }
    this.setupIntersectionObserver();
    if (process.client) {
      this.newComment.author.id = this.getNumericId();
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    getNumericId() {
      const authorId = localStorage.getItem('authorId');
      if (authorId) {
        return authorId;
      }
      // Generate a random 12-digit number
      const randomId = Math.floor(100000000000 + Math.random() * 900000000000).toString();
      localStorage.setItem('authorId', randomId);
      return randomId;
    },
    async fetchRelatedPosts(tagNames) {
      const relatedPostsResponse = await this.$apolloProvider.defaultClient.query({
        query: blogPostsQuery,
        variables: {
          locale: this.$i18n.locale,
        },
      });

      return relatedPostsResponse.data.blogPosts.data;
    },
    async submitComment() {
      this.formLoading = true;

      try {
        // Get the reCAPTCHA token
        const recaptchaToken = await this.$recaptcha.execute('submit_comment');
        const blogPostId = this.blogPost.id;

        await this.$apollo.mutate({
          mutation: createCommentMutation,
          variables: {
            input: {
              // Use content field as pipeline to send token to backend
              content: JSON.stringify({
                text: this.newComment.content,
                token: recaptchaToken,
              }),
              relation: `api::blog-post.blog-post:${blogPostId}`,
              author: {
                id: this.newComment.author.id,
                name: this.newComment.author.name,
              },
            },
          },
        });

        // Clear the form
        this.newComment.author.name = '';
        this.newComment.content = '';

        this.formSubmitted = true

      } catch (error) {
        console.error('Error submitting comment:', error);
      } finally {
        this.formLoading = false;
      }
    },
    getPageData() {
      return this.blogPost;
    },
    getPageComments() {
      return this.comments;
    },
    generateId(title, index) {
      return title
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          // Remove leading numbers and any following hyphens or spaces
          .replace(/^\d+[-\s]+/, '')
        + `-${index}`;
    },
    setupIntersectionObserver() {
      const options = {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0.6 // 60% of the section is visible
      };

      this.observer = new IntersectionObserver(this.handleIntersect, options);

      // Observe each section
      this.$nextTick(() => {
        const sections = document.querySelectorAll('section[id]');
        sections.forEach(section => {
          this.observer.observe(section);
        });
      });
    },
    handleIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.activeSection = entry.target.id;
        }
      });
    },
    scrollTo(ID) {
      const target = "#" + ID
      this.$store.dispatch('triggerScrollTo', {target, offset: -100});
    }
  },
  beforeRouteLeave(to, from, next) {
    // Reset color as it doesn't automatically set after redirect (preview)
    this.$colorMode.preference = 'light';
    next();
  },
};
